import { PRICING_META_DESCRIPTION } from '../content/pricingFigures';

/**
 * SEO Configuration for CellCog
 * Default meta tags and Open Graph values
 */

export const SEO_DEFAULTS = {
  siteName: 'CellCog',
  title: 'CellCog — Hire an AI Employee for Any Role',
  description: "Hire AI employees for any role. You build the org chart. They manage each other.",
  keywords: 'AI employee, hire an AI employee, AI worker, 24/7 AI employee, AI employee for sales, AI employee for support, AI employee for recruiting, AI SDR, autonomous AI agent, AI super agent, multi-modal AI',
  url: 'https://cellcog.ai',
  image: 'https://cellcog.ai/og-image.png',
  twitterHandle: '@cellcogai',
  themeColor: '#2e7e7e',
};

/**
 * Page-specific SEO configurations
 */
export const PAGE_SEO = {
  home: {
    title: 'CellCog — Hire an AI Employee for Any Role',
    description: "Hire AI employees for marketing, sales outreach, research, support, and any role you need. You build the org chart; they manage each other and report back to you. Hiring is free; you pay only for the work they do.",
  },
  posts: {
    title: 'Posts - CellCog Community',
    description: 'Explore AI-generated content, research reports, marketing materials, and more created by the CellCog community.',
  },
  productUpdates: {
    title: 'Product Updates - CellCog',
    description: 'Stay updated with the latest features, improvements, and announcements from CellCog.',
  },
  login: {
    title: 'Log In',
    description: 'Log in to CellCog to run your AI employees and super agent: research, reports, dashboards, and more.',
  },
  signup: {
    title: 'Sign Up: Hire Your First AI Employee',
    description: 'Create your CellCog account. Try it free, no credit card needed, 200 free credits at signup. Hire an AI employee or run one prompt into finished work.',
  },
  projects: {
    title: 'Projects - CellCog',
    description: 'Manage your AI projects, collaborate with team members, and organize your research workflows.',
  },
  drive: {
    title: 'Drive - CellCog',
    description: 'Access and manage your AI-generated files, reports, and content in one place.',
  },
  avatars: {
    title: 'Avatars - CellCog',
    description: 'Create and manage AI avatars for personalized content generation and brand representation.',
  },
  referrals: {
    title: 'Referrals - CellCog',
    description: 'Invite friends to CellCog and earn rewards. Share the power of AI-driven productivity.',
  },
  profile: {
    title: 'Profile - CellCog',
    description: 'Manage your CellCog profile, settings, and preferences.',
  },

  organization: {
    title: 'Organization - CellCog',
    description: 'Manage your organization settings, team members, and billing.',
  },
  pricing: {
    title: 'CellCog Pricing — Plans Starting at $8/month',
    description: PRICING_META_DESCRIPTION,
  },
  aiEmployees: {
    // Snippet pass 2026-09-02 (Nitish, live; Iris's GSC brief CEL-2015): exact-match phrase first + ONE
    // differentiator, cost answer moved up so it survives Google's ~155-char cut. Category CTR read =
    // top-4 queries, 2 weeks before/after this deploy (sponsored slots + AI Overview cap the ceiling).
    title: 'AI Employees — Hire an AI Employee for Any Role | CellCog',
    description: 'Hire AI employees for marketing, sales, research, support, or any role. Hire one, or a team that manages itself. Pay only for the work they do.',
  },
  linear: {
    title: 'CellCog for Linear — The Multimodal AI Agent for Your Linear Workspace',
    description: "The first agent in Linear that isn't a coder. Assign Linear issues to @CellCog for research, presentations, PDFs, images, video, and interactive dashboards. 5-second setup.",
  },
  openclaw: {
    title: 'CellCog for OpenClaw — The Any-to-Any Execution Layer for Your Agents',
    description: 'CellCog is the any-to-any execution layer for OpenClaw agents. Delegate research, documents, images, video, audio, and dashboards — no framework rewrites, just one API key.',
  },
  privacyPolicy: {
    title: 'Privacy Policy - CellCog',
    description: 'Learn how CellCog collects, uses, and protects your personal information.',
  },
};

/**
 * Generate full page title with site name
 * @param {string} pageTitle - The page-specific title
 * @returns {string} Full title with site name
 */
export const getFullTitle = (pageTitle) => {
  if (!pageTitle || pageTitle === SEO_DEFAULTS.siteName) {
    return SEO_DEFAULTS.title;
  }
  return `${pageTitle} | ${SEO_DEFAULTS.siteName}`;
};

/**
 * Truncate description to SEO-friendly length
 * @param {string} description - The description text
 * @param {number} maxLength - Maximum length (default 160)
 * @returns {string} Truncated description
 */
export const truncateDescription = (description, maxLength = 160) => {
  if (!description) return SEO_DEFAULTS.description;
  if (description.length <= maxLength) return description;
  return description.substring(0, maxLength - 3) + '...';
};
